import { Routes, Route } from 'react-router-dom';
import { TamaguiProvider } from 'tamagui';

import LandingPage from './pages/LandingPage';
import UseCasesIndex from './pages/use-cases/UseCasesIndex';
import DevelopersPage from './pages/use-cases/DevelopersPage';
import ProvidersPage from './pages/use-cases/ProvidersPage';
import EnterprisePage from './pages/use-cases/EnterprisePage';
import PricingPage from './pages/PricingPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import StatioVsDiy from './pages/compare/StatioVsDiy';
import StatioVsRawMcp from './pages/compare/StatioVsRawMcp';
import GettingStartedPage from './pages/GettingStartedPage';
import SecurityPage from './pages/SecurityPage';
import AboutPage from './pages/AboutPage';
import McpGenPage from './pages/McpGenPage';
import DownloadPage from './pages/DownloadPage';
import NotFoundPage from './pages/NotFoundPage';
import KitPage from './pages/KitPage';
import BlogIndex from './pages/blog/BlogIndex';
import TutorialsIndex from './pages/tutorials/TutorialsIndex';
import TutorialPage from './pages/tutorials/TutorialPage';
import SectionHub from './pages/tutorials/SectionHub';
import ToolsIndex from './pages/tools/ToolsIndex';
import ToolPage from './pages/tools/ToolPage';
import CatalogIndex from './pages/catalog/CatalogIndex';
import ServerPage from './pages/catalog/ServerPage';
import { ScrollToTop } from './components/ScrollToTop';
import { GuidePage, PostPage } from './components/mdx/layouts';
import { LIVE_SECTIONS, PAGES, POSTS, TUTORIALS, TUTORIALS_PATH, postPath, sectionPath, tutorialPath } from './content';
import { TOOLS, TOOLS_PATH, toolPath } from './tools/registry';
import { CATALOG, CATALOG_PATH, serverPath } from './catalog';
import { config } from './tamagui.config';

function AppContent() {
  return (
    <>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/use-cases" element={<UseCasesIndex />} />
        <Route path="/use-cases/developers" element={<DevelopersPage />} />
        <Route path="/use-cases/providers" element={<ProvidersPage />} />
        <Route path="/use-cases/enterprise" element={<EnterprisePage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/compare/statio-vs-diy" element={<StatioVsDiy />} />
        <Route path="/compare/statio-vs-raw-mcp" element={<StatioVsRawMcp />} />
        <Route path="/download" element={<DownloadPage />} />

        {/* There are no /docs routes here any more. Documentation lives at
            docs.statio.dev, which is its own site with search, versioning and a
            content pipeline. Two documentation surfaces is how the changelog and
            the product model drifted apart earlier; one is the fix. The old
            paths are redirected in public/_redirects. */}
        <Route path="/getting-started" element={<GettingStartedPage />} />
        <Route path="/security" element={<SecurityPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/mcp-gen" element={<McpGenPage />} />
        <Route path="/kit" element={<KitPage />} />
        <Route path="/blog" element={<BlogIndex />} />

        {/* MDX pages and posts in src/content: one route per file. */}
        {PAGES.map((p) => (
          <Route key={p.path} path={p.path} element={<GuidePage page={p} />} />
        ))}
        {POSTS.map((p) => (
          <Route key={p.slug} path={postPath(p.slug)} element={<PostPage post={p} />} />
        ))}

        {/* Tutorials in src/content/tutorials: task-shaped how-tos. */}
        <Route path={TUTORIALS_PATH} element={<TutorialsIndex />} />
        {LIVE_SECTIONS.map((s) => (
          <Route key={s.id} path={sectionPath(s)} element={<SectionHub section={s} />} />
        ))}
        {TUTORIALS.map((t) => (
          <Route key={t.slug} path={tutorialPath(t.slug)} element={<TutorialPage tutorial={t} />} />
        ))}

        {/* Free tools, from src/tools/registry.ts. */}
        <Route path={TOOLS_PATH} element={<ToolsIndex />} />
        {TOOLS.map((t) => (
          <Route key={t.slug} path={toolPath(t.slug)} element={<ToolPage tool={t} />} />
        ))}

        {/* The public MCP server catalog, from src/catalog. */}
        <Route path={CATALOG_PATH} element={<CatalogIndex />} />
        {CATALOG.map((s) => (
          <Route key={s.slug} path={serverPath(s.slug)} element={<ServerPage server={s} />} />
        ))}

        {/* Last, and it must stay last: react-router picks the best match, but
            keeping the catch-all at the end makes the intent unmistakable.
            Without it a bad URL rendered an empty document, see NotFoundPage. */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </>
  );
}

// One theme, no runtime switching. The palette lives in tamagui.config.ts and
// its radius/elevation custom properties are static :root declarations in
// global.css, so corners and shadows are correct in the prerendered HTML
// before any JavaScript runs.
export default function App() {
  return (
    <TamaguiProvider config={config} defaultTheme="light">
      <AppContent />
    </TamaguiProvider>
  );
}
